import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { capacitacionAdminApi, apiError } from "../../../lib/api";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { CAP_ICONS } from "../../capacitaciones/CapIcon";
import { ResourcesEditor } from "./ResourcesEditor";

export const emptyCap = (orden = 0) => ({
  titulo: "", resumen: "", icono: "BookOpen", contenido: "", subsecciones: [], recursos: [], programas: [], orden, publicado: false,
  preguntas: Array.from({ length: 5 }, () => ({ pregunta: "", opciones: ["", "", "", ""], correcta: 0 })),
});

const QuestionEditor = ({ p, i, onChange, onRemove }) => (
  <div className="rounded-xl ring-1 ring-black/10 p-3 space-y-2" data-testid={`cap-edit-q-${i}`}>
    <div className="flex gap-2">
      <Input value={p.pregunta} placeholder={`Pregunta ${i + 1}`} onChange={(e) => onChange({ ...p, pregunta: e.target.value })} />
      <button onClick={onRemove} className="p-2 rounded-lg hover:bg-[#FDECEC] text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
    </div>
    {p.opciones.map((o, j) => (
      <label key={j} className="flex items-center gap-2 text-sm">
        <input type="radio" name={`q${i}`} checked={p.correcta === j} onChange={() => onChange({ ...p, correcta: j })} title="Respuesta correcta" />
        <Input value={o} placeholder={`Opción ${j + 1}`} onChange={(e) => onChange({ ...p, opciones: p.opciones.map((x, k) => (k === j ? e.target.value : x)) })} />
      </label>
    ))}
  </div>
);

export const ModuleEditor = ({ value, programas, onClose, onSaved }) => {
  const [f, setF] = useState({ recursos: [], ...value });
  const set = (k, v) => setF({ ...f, [k]: v });
  const listSet = (k, i, v) => set(k, f[k].map((x, j) => (j === i ? v : x)));

  const save = async () => {
    if (!f.titulo.trim()) return toast.error("El título es obligatorio.");
    if (f.preguntas.some((p) => !p.pregunta.trim() || p.opciones.some((o) => !o.trim()))) return toast.error("Completa todas las preguntas y opciones.");
    const keep = (rs) => (rs || []).filter((r) => r.titulo.trim() || r.url.trim()).map((r) => ({ titulo: r.titulo.trim(), url: r.url.trim() }));
    const recursos = keep(f.recursos);
    const subsecciones = f.subsecciones.map((x) => ({ ...x, recursos: keep(x.recursos) }));
    if ([...recursos, ...subsecciones.flatMap((x) => x.recursos)].some((r) => !/^https?:\/\//i.test(r.url))) return toast.error("Cada link debe empezar con http:// o https://");
    const payload = { ...f, recursos, subsecciones, orden: Number(f.orden) || 0 };
    try {
      f.id ? await capacitacionAdminApi.update(f.id, payload) : await capacitacionAdminApi.create(payload);
      toast.success("Módulo guardado."); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cap-editor">
        <DialogHeader><DialogTitle>{f.id ? "Editar módulo" : "Nuevo módulo"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-[1fr_100px_160px] gap-3">
          <div><Label>Título</Label><Input data-testid="cap-edit-title" value={f.titulo} onChange={(e) => set("titulo", e.target.value)} /></div>
          <div><Label>Orden</Label><Input type="number" value={f.orden} onChange={(e) => set("orden", e.target.value)} /></div>
          <div><Label>Icono</Label>
            <select value={f.icono} onChange={(e) => set("icono", e.target.value)} className="w-full h-9 rounded-md border px-2 text-sm">
              {Object.keys(CAP_ICONS).map((k) => <option key={k}>{k}</option>)}
            </select></div>
        </div>
        <div><Label>Resumen</Label><Input value={f.resumen} onChange={(e) => set("resumen", e.target.value)} /></div>
        <div><Label>Contenido (admite **negrita**, *cursiva*, ## subtítulos y - listas)</Label>
          <Textarea data-testid="cap-edit-content" rows={8} value={f.contenido} onChange={(e) => set("contenido", e.target.value)} /></div>
        <div className="rounded-xl bg-[#F7F8FB] p-3">
          <Label>Links del módulo (videos, documentos, páginas)</Label>
          <div className="mt-2"><ResourcesEditor testid="cap-edit-res" value={f.recursos} onChange={(v) => set("recursos", v)} /></div>
        </div>
        <div>
          <div className="flex items-center justify-between"><Label>Subsecciones</Label>
            <button data-testid="cap-edit-add-sub" onClick={() => set("subsecciones", [...f.subsecciones, { titulo: "", texto: "", recursos: [] }])} className="text-xs font-bold text-[#B99B2E] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar</button></div>
          <div className="space-y-3 mt-2">
            {f.subsecciones.map((s, i) => (
              <div key={i} className="rounded-xl ring-1 ring-black/10 p-3 space-y-2" data-testid={`cap-edit-sub-${i}`}>
                <div className="flex gap-2">
                  <Input value={s.titulo} placeholder="Título de la subsección" onChange={(e) => listSet("subsecciones", i, { ...s, titulo: e.target.value })} />
                  <button onClick={() => set("subsecciones", f.subsecciones.filter((_, j) => j !== i))} className="p-2 text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Textarea rows={2} value={s.texto} placeholder="Explicación" onChange={(e) => listSet("subsecciones", i, { ...s, texto: e.target.value })} />
                <ResourcesEditor testid={`cap-edit-sub-${i}-res`} value={s.recursos || []} onChange={(v) => listSet("subsecciones", i, { ...s, recursos: v })} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label>Programas asignados (ninguno = todos)</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {programas.map((p) => {
              const on = f.programas.includes(p);
              return <button key={p} onClick={() => set("programas", on ? f.programas.filter((x) => x !== p) : [...f.programas, p])}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ring-1 ${on ? "bg-[#0A1C30] text-white ring-[#0A1C30]" : "ring-black/10 text-[#4A5568]"}`}>{p}</button>;
            })}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between"><Label>Evaluación (marca la respuesta correcta)</Label>
            <button onClick={() => set("preguntas", [...f.preguntas, { pregunta: "", opciones: ["", "", "", ""], correcta: 0 }])} className="text-xs font-bold text-[#B99B2E] inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Pregunta</button></div>
          <div className="space-y-3 mt-2">
            {f.preguntas.map((p, i) => <QuestionEditor key={i} p={p} i={i} onChange={(v) => listSet("preguntas", i, v)} onRemove={() => set("preguntas", f.preguntas.filter((_, j) => j !== i))} />)}
          </div>
        </div>
        <DialogFooter className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm mr-auto"><Switch data-testid="cap-edit-published" checked={f.publicado} onCheckedChange={(v) => set("publicado", v)} /> Publicado <span className="text-xs text-[#6B7688]">(al publicar, se vuelve obligatorio para usuarios bloqueados)</span></label>
          <button data-testid="cap-edit-save" onClick={save} className="rounded-xl bg-[#0A1C30] text-white font-bold px-5 py-2.5">Guardar</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
